/**
 * AUT-301 — "is this hold still worth asking about", in ONE place.
 *
 * ## Why this is shared and not api-local
 *
 * Same reason `admin-payment-actions.ts` is: the capture button and the server
 * that honours it must agree. Until now they did not even disagree — only the
 * admin SPA had an opinion. `packages/admin/src/pages/orders/_lib/
 * captureEligibility.ts` hid the button on an expired hold; the server's
 * `captureNow` gated on `payment.status !== 'authorized'` and NOTHING else, so
 * the only thing standing between an operator and a settlement request against
 * a dead authorization was a client-side render decision. A stale tab, a curl,
 * or a future screen that forgot the rule walks straight past it.
 *
 * ## What the window is, and what it is NOT
 *
 * `Payment.holdExpiresAt` is computed as `authorizedAt + escrow.holdDays`, and
 * `escrow.holdDays` is OUR escrow policy (it also computes
 * `releaseScheduledFor`), not the bank's authorization lifetime. The two
 * numbers have never been compared. So this predicate answers "has our own
 * deadline passed" — a scheduling question — and it must never be the sole
 * basis for moving or refusing to move money without asking the provider what
 * it actually thinks. The provider is the fact; this is the calendar.
 *
 * Practically: it may decide WHEN TO ASK and WHEN TO ESCALATE. Both callers
 * treat a closed window as a reason to consult the provider, not as a verdict.
 */
/**
 * How long before `holdExpiresAt` a live hold counts as urgent.
 *
 * The stranded-hold sweep uses it to decide when a hold that nothing has tried
 * to settle is worth a human's attention: 24 hours is roughly the shortest
 * window in which an operator can realistically be found, read the alert, and
 * press a button before the bank releases the money on its own.
 */
export const HOLD_WINDOW_CLOSING_BEFORE_MS = 24 * 60 * 60 * 1000;
/**
 * Milliseconds, or `null` when there is no usable deadline.
 *
 * `null` covers three real cases and treats them identically on purpose: a
 * single-stage payment (no hold ever existed), a two-stage row written before
 * the column was populated, and an unparseable value. In all three we do not
 * know when the window closes, and "we do not know" must never read as
 * "expired" — that would turn a missing timestamp into a refusal to take
 * money the customer owes.
 */
function expiryMillis(holdExpiresAt) {
    if (holdExpiresAt === null || holdExpiresAt === undefined)
        return null;
    const parsed = holdExpiresAt instanceof Date ? holdExpiresAt.getTime() : Date.parse(holdExpiresAt);
    return Number.isNaN(parsed) ? null : parsed;
}
/**
 * True when our recorded hold window has already elapsed.
 *
 * `now` is injectable so callers and specs do not depend on wall-clock.
 */
export function isHoldWindowClosed(holdExpiresAt, now = Date.now()) {
    const expiry = expiryMillis(holdExpiresAt);
    if (expiry === null)
        return false;
    return expiry <= now;
}
/**
 * True when the window is inside its final stretch (or already gone).
 *
 * Deliberately inclusive of "already closed": a caller asking "should someone
 * look at this now" wants a yes in both cases, and forcing every one of them to
 * write `isClosing(x) || isClosed(x)` is how the two predicates drift apart.
 */
export function isHoldWindowClosing(holdExpiresAt, now = Date.now(), leadMs = HOLD_WINDOW_CLOSING_BEFORE_MS) {
    const expiry = expiryMillis(holdExpiresAt);
    if (expiry === null)
        return false;
    return expiry - leadMs <= now;
}
/**
 * Whole milliseconds left in the window; `null` when there is no deadline, and
 * negative once it has passed.
 *
 * Exposed for log lines and operator-facing copy — "expired 6 hours ago" is a
 * far more actionable sentence than "expired".
 */
export function holdWindowRemainingMs(holdExpiresAt, now = Date.now()) {
    const expiry = expiryMillis(holdExpiresAt);
    if (expiry === null)
        return null;
    return expiry - now;
}
//# sourceMappingURL=payment-hold-window.js.map